import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Info, ArrowLeft } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { US_STATES, STATE_TIMEZONES } from "@/lib/locationData";
import { ReferralWelcomeModal } from "@/components/ReferralWelcomeModal";
import { TrialWelcomeModal } from "@/components/TrialWelcomeModal";
import { apiClient } from "@/shared/services/api";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";

export default function ProfileSetupPage() {
  const { refreshUserData, isLoading: authLoading, session } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [hasPendingInvite, setHasPendingInvite] = useState(false);
  const [inviteCompanyName, setInviteCompanyName] = useState("");
  const [inviteDetails, setInviteDetails] = useState<any>(null);
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const [hasReferral, setHasReferral] = useState(false);
  const [referralInfo, setReferralInfo] = useState<any>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkProfileAndInvite = async () => {
      try {
        // Wait for auth to be ready before making API calls
        if (authLoading) {
          console.log('[PROFILE SETUP] ⏳ Waiting for auth to complete...');
          return;
        }

        if (!session) {
          console.log('[PROFILE SETUP] ❌ No session found, redirecting to auth');
          navigate('/auth');
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/auth');
          return;
        }

        // Pre-fill from metadata (works for both email and Google signup)
        const metadata = user.user_metadata || {};

        // CRITICAL FIX: Parallelize API calls instead of sequential
        // This makes profile setup faster and more resilient to failures
        console.log('[PROFILE SETUP] ✅ Auth ready, fetching user data and pending invite in parallel...');

        const [userDataResult, inviteDataResult] = await Promise.allSettled([
          apiClient.get<any>(API_ROUTES.USER_ME),
          apiClient.get<any>(API_ROUTES.USER_PENDING_INVITE)
        ]);

        // Handle user data result
        if (userDataResult.status === 'fulfilled') {
          const userData = userDataResult.value;

          const user = userData?.user;

          console.log('[PROFILE SETUP] API Response:', {
            hasUser: !!user,
            profile_setup_complete: user?.profile_setup_complete,
            hasCompany: !!userData?.company,
            companyId: userData?.company?.id,
            hasPersonalDetails: !!(user?.first_name && user?.last_name && user?.phone),
            firstName: user?.first_name,
            lastName: user?.last_name,
            phone: user?.phone
          });

          // ✅ STRICT CHECK: Profile must be truly complete (matches OAuth callback logic)
          // Must have: profile_setup_complete=true, company exists, AND personal details filled
          const isProfileComplete = user?.profile_setup_complete &&
                                    userData?.company &&
                                    user?.first_name &&
                                    user?.last_name &&
                                    user?.phone;

          if (isProfileComplete) {
            console.log('[PROFILE SETUP] ✅ Profile already complete, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          } else {
            console.log('[PROFILE SETUP] ❌ Profile incomplete:', {
              profile_setup_complete: user?.profile_setup_complete,
              hasCompany: !!userData?.company,
              hasFirstName: !!user?.first_name,
              hasLastName: !!user?.last_name,
              hasPhone: !!user?.phone
            });
          }
        } else {
          console.error('[PROFILE SETUP] ❌ Profile check failed:', userDataResult.reason);
          // Continue with profile setup if check fails (user might be new)
        }

        // Handle company name and metadata
        if (metadata.company_name) setCompanyName(metadata.company_name);
        if (metadata.phone_number) setPhoneNumber(metadata.phone_number.replace(/\D/g, ''));
        if (metadata.phone && !metadata.phone_number) setPhoneNumber(metadata.phone.replace(/\D/g, ''));
        if (metadata.state) setState(metadata.state);
        if (metadata.zip_code) setZipCode(metadata.zip_code);

        // Parse names - Google provides given_name/family_name, email signup provides first_name/last_name
        if (metadata.given_name) setFirstName(metadata.given_name);
        if (metadata.family_name) setLastName(metadata.family_name);
        if (metadata.first_name && !metadata.given_name) setFirstName(metadata.first_name);
        if (metadata.last_name && !metadata.family_name) setLastName(metadata.last_name);

        // Fallback: parse from full_name or name
        if (!firstName && !lastName && (metadata.full_name || metadata.name)) {
          const parts = (metadata.full_name || metadata.name).trim().split(' ');
          if (!firstName) setFirstName(parts[0] || '');
          if (!lastName) setLastName(parts.slice(1).join(' ') || '');
        }

        // Handle pending invite result
        if (inviteDataResult.status === 'fulfilled') {
          const inviteData = inviteDataResult.value;

          console.log('[PROFILE SETUP] Pending invite check:', {
            hasInvite: inviteData?.has_invite,
            companyName: inviteData?.company_name,
            invitedBy: inviteData?.invited_by_name,
            inviteData
          });

          if (inviteData?.has_invite) {
            setHasPendingInvite(true);
            setInviteCompanyName(inviteData.company_name || 'a team');
            setInviteDetails(inviteData);

            toast({
              title: "Team Invitation Found!",
              description: `You'll join ${inviteData.company_name} after completing your profile.`,
            });
          }
        } else {
          console.log('[PROFILE SETUP] No pending invite found or request failed:', inviteDataResult.reason);
        }

        // ✅ FIXED: Validate referral in background (non-blocking)
        const referralCode = metadata.referral_code || localStorage.getItem('pendingReferralCode');
        if (referralCode && !hasPendingInvite) {
          // Don't block UI - validate in background
          console.log('[PROFILE SETUP] 🔍 Validating referral code in background:', referralCode);

          // Fire-and-forget validation (runs in background)
          apiClient.post<any>(API_ROUTES.USER_VALIDATE_REFERRAL, {
            referral_code: referralCode,
          })
          .then(referralData => {
            console.log('[PROFILE SETUP] Referral validation response:', {
              valid: referralData?.valid,
              referralCode,
              referrerName: referralData?.referral?.referrer_name,
              referralInfo: referralData?.referral
            });

            if (referralData?.valid && referralData?.referral) {
              setHasReferral(true);
              setReferralInfo(referralData.referral);
              console.log('[PROFILE SETUP] ✅ Referral code validated successfully:', {
                referrerName: referralData.referral.referrer_name,
                referralCode: referralData.referral.referral_code
              });
            } else {
              console.log('[PROFILE SETUP] ❌ Referral code invalid or expired');
            }
          })
          .catch(error => {
            console.error('[PROFILE SETUP] ❌ Referral validation error:', {
              error: error?.message || error,
              referralCode
            });
            // Even if validation fails, continue with regular flow
          });
        } else {
          console.log('[PROFILE SETUP] ℹ️ No referral code found in metadata or localStorage');
        }

      } catch (error) {
        console.error('Error checking profile:', error);
      } finally {
        setCheckingProfile(false);
      }
    };

    checkProfileAndInvite();
  }, [navigate, toast, authLoading, session]);

  const validateForm = async (): Promise<boolean> => {
    const errors: any = {};

    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!hasPendingInvite && !companyName.trim()) errors.companyName = "Company name is required";

    // Phone validation
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    if (!phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
    } else if (cleanedPhone.length !== 10) {
      errors.phoneNumber = "Phone must be 10 digits";
    } else {
      // Check if phone number already exists
      try {
        const response = await apiClient.post<{
          ok: boolean;
          exists: boolean;
          message?: string;
        }>('/user/check-phone', { phone: `+1${cleanedPhone}` });

        if (response.ok && response.exists) {
          errors.phoneNumber = response.message || 'This phone number is already registered';
        }
      } catch (err) {
        // Non-critical - allow to proceed on error
        console.warn('Phone check failed:', err);
      }
    }

    if (!state.trim()) errors.state = "State is required";
    if (!zipCode.trim()) errors.zipCode = "Zip code is required";
    else if (zipCode.length !== 5) errors.zipCode = "Zip code must be 5 digits";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const fullNameCombined = `${firstName} ${lastName}`.trim();

      // ✅ PERFORMANCE FIX: Run auth + profile updates in parallel (saves 1-2s)
      // These operations are independent and don't need to be sequential
      console.log('[PROFILE SETUP] Running auth + profile updates in parallel...');
      const [authResult, profileResult] = await Promise.allSettled([
        supabase.auth.updateUser({
          data: {
            full_name: fullNameCombined,
            first_name: firstName,
            last_name: lastName,
            company_name: hasPendingInvite ? inviteCompanyName : companyName,
            phone: phoneNumber,
            phone_number: phoneNumber,
            state,
            country: 'USA',
            zip_code: zipCode,
            timezone: STATE_TIMEZONES[state] || "EST (UTC-5)"
          }
        }),
        apiClient.put(API_ROUTES.USER_PROFILE, {
          first_name: firstName,
          last_name: lastName,
          name: fullNameCombined,
          phone: phoneNumber,
          state,
          country: 'USA',
          zip_code: zipCode,
          timezone: STATE_TIMEZONES[state] || "EST (UTC-5)",
          profile_setup_complete: true,
        })
      ]);

      // Check for errors in parallel operations
      if (authResult.status === 'rejected') {
        console.error('[PROFILE SETUP] Auth update failed:', authResult.reason);
        throw authResult.reason;
      }
      if (authResult.value.error) {
        console.error('[PROFILE SETUP] Auth update error:', authResult.value.error);
        throw authResult.value.error;
      }
      if (profileResult.status === 'rejected') {
        console.error('[PROFILE SETUP] Profile update failed:', profileResult.reason);
        throw profileResult.reason;
      }
      console.log('[PROFILE SETUP] ✅ Auth + profile updates completed in parallel');

      // Handle invite acceptance
      if (hasPendingInvite && inviteDetails) {
        console.log('[PROFILE SETUP] ✅ INVITED USER - Accepting team invite:', {
          companyName: inviteCompanyName,
          invitedBy: inviteDetails.invited_by_name,
          inviteId: inviteDetails.invite_id
        });

        try {
          await apiClient.post(API_ROUTES.USER_ACCEPT_INVITE, {
            email: user.email,
            company_name: companyName,
          });

          console.log('[PROFILE SETUP] ✅ INVITED USER - Successfully joined team, navigating to dashboard');

          toast({
            title: "Joined Team!",
            description: `You've been added to ${inviteCompanyName}`,
          });

          localStorage.removeItem('pendingOAuthSetup');
          localStorage.removeItem('pendingTrialCheckout');
          localStorage.removeItem('pendingReferralCode');

          navigate('/dashboard', { replace: true });
          return;
        } catch (error) {
          console.error('[PROFILE SETUP] ❌ INVITED USER - Invite accept error:', error);
          // Continue with regular flow if invite fails
        }
      }

      // Initialize company via backend API FIRST (needed for referral application)
      let companyId: string | null = null;
      try {
        const companyResult = await apiClient.post<{ company_id?: string }>(API_ROUTES.USER_INITIALIZE_COMPANY, {
          company_name: companyName,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          state,
          zip_code: zipCode,
        });
        companyId = companyResult?.company_id || null;
        console.log('[PROFILE SETUP] ✅ Company initialized:', companyId);
      } catch (error) {
        console.error('Company initialization error:', error);
        throw new Error('Failed to create company');
      }

      // Apply referral code if exists (AFTER company creation)
      // The apply_referral endpoint will validate via Referral Rocket API
      const referralCode = user.user_metadata?.referral_code || localStorage.getItem('pendingReferralCode');
      // Use local variables to avoid stale React state in the modal decision below
      let referralApplied = false;
      let appliedReferralInfo: any = null;
      if (referralCode) {
        console.log('🎁 Applying referral code:', referralCode);

        try {
          const applyResult = await apiClient.post<{
            success: boolean;
            message: string;
            data?: any;
          }>(API_ROUTES.USER_APPLY_REFERRAL, {
            user_id: user.id,
            company_id: companyId,
            email: user.email,
            referral_code: referralCode,
            skip_trial: true,
          });
          console.log('✅ Referral applied successfully:', applyResult);

          if (applyResult?.success) {
            referralApplied = true;
            appliedReferralInfo = applyResult?.data || {};
            setHasReferral(true);
            setReferralInfo(appliedReferralInfo);
          }
        } catch (error) {
          console.error('Referral application error:', error);
          // Continue with signup even if referral fails
        }

        localStorage.removeItem('pendingReferralCode');
      }

      // CRITICAL FIX: Use Promise.allSettled instead of Promise.all
      // This prevents one failure from blocking the other
      console.log('[PROFILE SETUP] Starting background data refresh...');
      const refreshPromise = Promise.allSettled([
        refreshUserData(),
        refreshSubscription()
      ]).then((results) => {
        // Check individual results
        const [userDataResult, subscriptionResult] = results;

        if (userDataResult.status === 'rejected') {
          console.error('[PROFILE SETUP] ❌ User data refresh failed:', userDataResult.reason);
        } else {
          console.log('[PROFILE SETUP] ✅ User data refreshed');
        }

        if (subscriptionResult.status === 'rejected') {
          console.error('[PROFILE SETUP] ❌ Subscription refresh failed:', subscriptionResult.reason);
        } else {
          console.log('[PROFILE SETUP] ✅ Subscription refreshed');
        }

        // Signal AppLoadingGate that setup is complete and modal is about to show
        // This keeps the loading screen visible until the modal is ready
        window.dispatchEvent(new Event('profile-setup-complete'));

        // Use local variables (not React state) to avoid stale closure race condition
        if (referralApplied && appliedReferralInfo) {
          console.log('[PROFILE SETUP] ✅ REFERRED USER - Showing ReferralWelcomeModal');
          setShowReferralModal(true);
        } else {
          console.log('[PROFILE SETUP] ✅ REGULAR USER - Showing TrialWelcomeModal');
          setShowTrialModal(true);
        }
      }).catch((error) => {
        // This should never happen with allSettled, but just in case
        console.error('[PROFILE SETUP] ❌ Unexpected error in refresh:', error);
        // Still dispatch event to prevent infinite loading
        window.dispatchEvent(new Event('profile-setup-complete'));
      });

      // Navigate to dashboard IMMEDIATELY (don't wait for refresh)
      // This shows AppLoadingGate's "Loading wlkthru - Setting up your dashboard..." loading screen
      // While the data refresh happens in background
      console.log('[PROFILE SETUP] Navigating to dashboard...');
      navigate('/dashboard', { replace: true, state: { fromProfileSetup: true } });

      // Note: refreshPromise continues in background after navigation

    } catch (error: any) {
      console.error('Profile setup error:', error);
      toast({
        title: "Setup Error",
        description: error.message || "Failed to complete profile setup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    // Warn user that profile setup is mandatory
    const confirmSignOut = window.confirm(
      'Your profile setup is incomplete. You must complete your profile to access the dashboard. Are you sure you want to sign out?'
    );

    if (!confirmSignOut) {
      return;
    }

    await supabase.auth.signOut();
    localStorage.clear();
    navigate('/auth');
  };

  const handleReferralModalClose = () => {
    setShowReferralModal(false);

    toast({
      title: "Welcome to Wlkthru!",
      description: "Your account is ready. Visit the subscription page to activate your discount.",
    });

    localStorage.removeItem('pendingOAuthSetup');
    localStorage.removeItem('pendingTrialCheckout');
    localStorage.removeItem('pendingReferralCode');

    navigate('/dashboard', { replace: true });
  };

  const handleTrialModalClose = () => {
    console.log('[PROFILE SETUP] Trial modal closed - navigating to dashboard');
    setShowTrialModal(false);

    toast({
      title: "Welcome to Wlkthru!",
      description: "Your 15-day free trial has started. Enjoy all premium features!",
    });

    localStorage.removeItem('pendingOAuthSetup');
    localStorage.removeItem('pendingTrialCheckout');
    localStorage.removeItem('pendingReferralCode');

    navigate('/dashboard', { replace: true });
  };

  // Don't render anything while checking if profile is already complete
  // This prevents flash - AuthPage loading screen stays visible
  if (checkingProfile) {
    return null;
  }

  return (
    <>
      {/* Loading Modal - Generic loading (form submission, etc) */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="relative w-16 h-16 mx-auto">
              {/* Spinning loader */}
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">Loading wlkthru</h2>
              <p className="text-sm text-muted-foreground">Setting up your account...</p>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl bg-white shadow-lg border border-gray-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" size="icon" onClick={handleSignOut} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Complete Your Profile</h1>
                <p className="text-sm text-gray-600">Just a few more details to get started</p>
              </div>
            </div>

            {hasPendingInvite && (
              <Alert className="mb-6 bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-900">
                  <strong>You've been invited to join {inviteCompanyName}!</strong>
                  {inviteDetails?.invited_by_name && (
                    <>
                      <br />
                      <span className="text-sm">Invited by: <strong>{inviteDetails.invited_by_name}</strong></span>
                    </>
                  )}
                  <br />
                  <span className="text-sm">Complete your profile to access your team workspace.</span>
                </AlertDescription>
              </Alert>
            )}

            {hasReferral && referralInfo && !hasPendingInvite && (
              <Alert className="mb-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                <Info className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  <strong>You're signing up with a referral!</strong>
                  <br />
                  <span className="text-sm">
                    Referred by: <strong>{referralInfo.referrer_name || 'a Wlkthru partner'}</strong>
                  </span>
                  <br />
                  <span className="text-sm font-semibold text-green-700">Save $100 over 3 months!</span>
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={fieldErrors.firstName ? "border-red-500" : ""}
                  />
                  {fieldErrors.firstName && <p className="text-xs text-red-500 mt-1">{fieldErrors.firstName}</p>}
                </div>

                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={fieldErrors.lastName ? "border-red-500" : ""}
                  />
                  {fieldErrors.lastName && <p className="text-xs text-red-500 mt-1">{fieldErrors.lastName}</p>}
                </div>
              </div>

              {!hasPendingInvite && (
                <div>
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className={fieldErrors.companyName ? "border-red-500" : ""}
                  />
                  {fieldErrors.companyName && <p className="text-xs text-red-500 mt-1">{fieldErrors.companyName}</p>}
                </div>
              )}

              {hasPendingInvite && (
                <div>
                  <Label>Company</Label>
                  <Input
                    value={inviteCompanyName}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-green-600 mt-1">✓ You're joining this company</p>
                </div>
              )}

              <div>
                <Label htmlFor="phoneNumber">Phone Number * <span className="text-muted-foreground">(US)</span></Label>
                <div className="flex gap-2">
                  <Input value="+1" disabled className="w-16 bg-gray-50" />
                  <Input
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="5555551234"
                    className={fieldErrors.phoneNumber ? "border-red-500 flex-1" : "flex-1"}
                  />
                </div>
                {fieldErrors.phoneNumber && <p className="text-xs text-red-500 mt-1">{fieldErrors.phoneNumber}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger className={fieldErrors.state ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.state && <p className="text-xs text-red-500 mt-1">{fieldErrors.state}</p>}
                </div>

                <div>
                  <Label htmlFor="zipCode">Zip Code *</Label>
                  <Input
                    id="zipCode"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="12345"
                    className={fieldErrors.zipCode ? "border-red-500" : ""}
                  />
                  {fieldErrors.zipCode && <p className="text-xs text-red-500 mt-1">{fieldErrors.zipCode}</p>}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#94ACF8] hover:bg-[#7B93E0] text-white font-semibold py-6 rounded-lg transition-all duration-200"
                disabled={loading}
              >
                {loading ? 'Setting up your account...' : 'Complete Setup'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <ReferralWelcomeModal
        open={showReferralModal}
        onClose={handleReferralModalClose}
        referrerName={
          referralInfo?.referrer_name &&
          referralInfo.referrer_name !== 'Unknown' &&
          referralInfo.referrer_name !== 'A friend' &&
          referralInfo.referrer_name.trim()
            ? referralInfo.referrer_name
            : undefined // ✅ Pass undefined if no valid name
        }
        discountAmount={100}
        discountMonths={3}
      />

      <TrialWelcomeModal
        open={showTrialModal}
        onOpenChange={handleTrialModalClose}
        trialDays={15}
      />
    </>
  );
}
